import { Button, Infobox, Tooltip } from 'Atoms';
import { AggregatedQualitativeAnswers } from '../../AggregatedMetrics/AggregatedMetrics.hooks';
import { MetricsTableData, TimePeriodsEnums } from '../../..';
import { useEffect, useMemo, useRef, useState } from 'react';
import { summarizeNarrativeAnswers } from '../../MetricAI/AIRequestFunctions';
import { useCurrentCompany, useToast } from 'utils/hooks';
import { Typography } from 'Tokens';
import {
  HStack,
  MenuButton,
  VStack,
  useToast as useChakraToast,
  useDisclosure,
} from '@chakra-ui/react';
import { AIIcon, RefreshIcon, RemoveIcon } from 'Tokens/Icons/Function';
import { usePopulateMetricSummary } from './AnswersSummarizer.hook';
import { useFeatureFlags } from 'containers/Navigation';
import { Menu } from 'Molecules/Menu';
import { ChevronDownIcon } from 'Tokens/Icons/Direction';
import { AIProgressModal, InteractionBlocker } from '../../MetricAI/AIUtils';
import { Language } from 'utils/language';
import { GeneratedSummaryStatus } from '../../Metrics';
import { DatapointFieldsFragment_, EsrsAnswersFieldsFragment_ } from 'models';

const cleanUpHTMLText = (text: string) => {
  return (
    text
      .replace(/(<([^>]+)>)/gi, ' ')
      .replace(/\n/g, ' ')
      .trim() ?? ''
  );
};

export const AnswersSummarizer = ({
  metric,
  answer,
  answersData,
  datapoint,
  companyReportingUnit,
  generatedSummaryStatus,
  setGeneratedSummaryStatus,
}: {
  metric: MetricsTableData['metric'];
  answer: EsrsAnswersFieldsFragment_ | undefined;
  answersData: AggregatedQualitativeAnswers[number];
  datapoint: DatapointFieldsFragment_ | undefined;
  companyReportingUnit?: string;
  generatedSummaryStatus?: GeneratedSummaryStatus;
  setGeneratedSummaryStatus?: React.Dispatch<React.SetStateAction<GeneratedSummaryStatus>>;
}) => {
  const company = useCurrentCompany();
  const { hasAiAccess } = useFeatureFlags(company);

  const aiConfig = useMemo(() => {
    return {
      preferredLanguage: company.company?.aiConfig?.preferredLanguage ?? Language.EnglishUK,
      toneOfVoice: company.company?.aiConfig?.toneOfVoice ?? '',
    };
  }, [company.company?.aiConfig]);

  const { preferredLanguage, toneOfVoice } = aiConfig;

  const abortControllerRef = useRef<AbortController | null>(null);

  const [generatedSummary, setGeneratedSummary] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  const isApproved = useMemo(() => {
    return (
      (generatedSummaryStatus?.isApproved &&
        generatedSummaryStatus.metricRef === metric.reference) ??
      false
    );
  }, [generatedSummaryStatus, metric]);

  const hasGeneratedSummary = useMemo(() => {
    return !!generatedSummary && generatedSummaryStatus?.metricRef === metric.reference;
  }, [generatedSummary, generatedSummaryStatus, metric]);

  const [showMore, setShowMore] = useState(false);
  const numberOfLines = useMemo(
    () => Math.ceil((generatedSummary?.length ?? 1) / 48),
    [generatedSummary]
  );

  const toast = useToast();
  const chakraToast = useChakraToast();

  const isForBusinessUnits = useMemo(() => !!answersData?.reportingUnits, [answersData]);

  const metricAnswers = useMemo(() => {
    return {
      metricRef: answersData?.metricRef,
      metricTitle: metric?.title,
      metricDescription: cleanUpHTMLText(metric?.description ?? ''),
      answers: isForBusinessUnits
        ? (answersData.reportingUnits?.map((r) => ({
            unitName: r.reportingUnit.name,
            answer:
              r.answer?.datapoints.find((dp: any) => dp.timeframe === TimePeriodsEnums.year)
                ?.value ?? 'N/A',
          })) ?? [])
        : (answersData.subsidiaries?.map((s) => ({
            unitName: s.subsidiary.company.name,
            answer:
              s.answer?.datapoints.find((dp: any) => dp.timeframe === TimePeriodsEnums.year)
                ?.value ?? 'N/A',
          })) ?? []),
    };
  }, [answersData, metric, answersData, isForBusinessUnits]);

  const { populateAnswer } = usePopulateMetricSummary({
    answer,
    metricRef: metric.reference,
    datapoint,
  });

  const generateAggregatedNarrativeAnswers = async () => {
    if (!metricAnswers.answers || metricAnswers.answers.length === 0) {
      throw new Error('No answers provided');
    }

    const timedToastId = toast({
      text: 'AI is writing a summary for a data point...',
      closable: true,
      duration: null,
    });

    try {
      setIsGenerating(true);

      abortControllerRef.current = new AbortController();

      const summarizedAnswer = await summarizeNarrativeAnswers({
        metricTitle: metricAnswers.metricTitle,
        metricDescription: metricAnswers.metricDescription ?? '',
        answers: metricAnswers.answers,
        isForBusinessUnits,
        preferredLanguage,
        toneOfVoice,
        requestSignal: abortControllerRef.current.signal,
      });

      const aggregatedAnswerData = summarizedAnswer.data.summary;

      setIsGenerating(false);
      chakraToast.close(timedToastId);

      return aggregatedAnswerData;
    } catch (error) {
      setIsGenerating(false);

      chakraToast.close(timedToastId);

      if (abortControllerRef.current?.signal.aborted) {
        toast({
          text: 'Summary generation cancelled',
        });
      } else {
        toast({
          variant: 'danger',
          text: 'Couldn’t generate summary. Write your summary manually',
        });
        console.error(error);
      }
    }
  };

  const handleGenerate = async () => {
    try {
      const summary = await generateAggregatedNarrativeAnswers();
      setGeneratedSummary(summary);
      setGeneratedSummaryStatus?.({
        isApproved: false,
        metricRef: metricAnswers.metricRef ?? '',
      });
    } catch {
      toast({
        text: isForBusinessUnits
          ? 'No business unit answers provided'
          : 'No subsidiary answers provided',
      });
    }
  };

  const onApprove = async () => {
    try {
      await populateAnswer(generatedSummary, companyReportingUnit ?? '');
      setGeneratedSummaryStatus?.({
        isApproved: true,
        metricRef: metricAnswers.metricRef ?? '',
      });
    } catch {
      toast({
        text: 'Failed to write answer, please try again',
      });
    }
  };

  const {
    isOpen: isAIProgressModalOpen,
    onClose: onAIProgressModalClose,
    onOpen: onAIProgressModalOpen,
  } = useDisclosure();

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (isGenerating) {
        // Check if the click is on the InteractionBlocker
        const blocker = document.getElementById('interaction-blocker');
        if (blocker && blocker.contains(e.target as Node)) {
          onAIProgressModalOpen();
        }
      }
    };

    if (isGenerating) {
      window.addEventListener('click', handleClick);
    } else {
      window.removeEventListener('click', handleClick);
      onAIProgressModalClose();
    }

    return () => {
      window.removeEventListener('click', handleClick);
    };
  }, [isGenerating, onAIProgressModalOpen, onAIProgressModalClose]);
  return (
    <>
      <Infobox
        status="info"
        closable={false}
        withIcon={false}
        title={
          isForBusinessUnits
            ? 'Summarize answers from business units'
            : 'Summarize answers from subsidiaries'
        }
        description={
          <VStack spacing="20px" alignItems="start">
            <Typography variant="body">
              {isForBusinessUnits
                ? 'This answer could be generated automatically based on answers from business units'
                : 'This answer could be generated automatically based on answers from subsidiaries'}
            </Typography>

            {!hasGeneratedSummary ? (
              <Tooltip
                label={'Contact us if you would like to use this feature'}
                isDisabled={hasAiAccess}
              >
                <Button
                  isLoading={isGenerating}
                  leftIcon={<AIIcon color="inherit" />}
                  onClick={handleGenerate}
                  isDisabled={!hasAiAccess}
                >
                  Generate answer
                </Button>
              </Tooltip>
            ) : (
              <VStack
                spacing="16px"
                alignItems="start"
                borderTop="1px solid"
                borderColor="border.decorative"
                pt="16px"
              >
                <VStack spacing="4px" alignItems="start">
                  <HStack spacing="6px">
                    <AIIcon boxSize="18px" color="text.info" />
                    <Typography variant="h3" color="text.info">
                      Answer was generated
                    </Typography>
                  </HStack>
                  <Typography variant="body" color="text.info">
                    Suggested answer will not be saved until it is approved:
                  </Typography>
                </VStack>

                {/* Summary display */}
                <VStack spacing="4px" w="100%" alignItems="start">
                  <Typography variant="body" noOfLines={showMore ? undefined : 5}>
                    {generatedSummary}
                  </Typography>
                  {numberOfLines > 5 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      alignVertically={true}
                      onClick={() => setShowMore(!showMore)}
                    >
                      {showMore ? 'Show less' : 'Show more'}
                    </Button>
                  )}
                </VStack>

                {/* Approval */}
                <HStack spacing="10px">
                  {isApproved ? (
                    <Button selected>Approved</Button>
                  ) : (
                    <Button variant="primary" isDisabled={isGenerating} onClick={onApprove}>
                      Approve
                    </Button>
                  )}

                  <Menu
                    gutter={2}
                    usePortal
                    menuButton={
                      <MenuButton
                        as={Button}
                        rightIcon={<ChevronDownIcon color="inherit" />}
                        size="md"
                        variant="ghost"
                        aria-label="share"
                      >
                        More options
                      </MenuButton>
                    }
                    sections={[
                      {
                        actions: [
                          {
                            id: 'try-again',
                            title: 'Try again',
                            leftElement: <RefreshIcon />,
                            onClick: () => {
                              handleGenerate();
                            },
                          },
                          {
                            id: 'discard',
                            title: 'Discard',
                            leftElement: <RemoveIcon />,
                            onClick: () => {
                              setGeneratedSummary('');
                            },
                          },
                        ],
                      },
                    ]}
                  />
                </HStack>
              </VStack>
            )}
          </VStack>
        }
      />

      <AIProgressModal
        isOpen={isAIProgressModalOpen}
        onClose={onAIProgressModalClose}
        setIsGenerating={setIsGenerating}
        cancelRequest={() => {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
        }}
      />

      {isGenerating && <InteractionBlocker id="interaction-blocker" />}
    </>
  );
};
